import { useTranslation } from 'next-i18next';

import { useMemo, useState } from 'react';

import {
    Box,
    Button,
    Flex,
    HStack,
    Separator,
    Skeleton,
    SkeletonText,
    useBreakpointValue,
} from '@chakra-ui/react';

import { LoadingState } from '@nocowanie/core/enums';

import { SingleApartment } from '@app/components/molecules/single-apartment';
import { BestOffer } from '@app/components/organisms/best-offer';
import { SKELETON_PROPS, VISIBLE_PRICING_VARIANTS } from '@app/consts';
import { AppModalsEnum } from '@app/enums';
import { useDrawerState } from '@app/hooks/useDrawerState';
import { useIsUnavailable } from '@app/hooks/useIsUnavailable';
import * as accommodationSlice from '@app/state/slices/accommodation.slice';
import * as accommodationStaticSlice from '@app/state/slices/accommodation-static.slice';
import * as commonUiStateSlice from '@app/state/slices/common-ui-state.slice';
import { useAppDispatch, useAppSelector } from '@app/state/state';

import { AccommodationPricingProps } from './accommodation-pricing.props';

const SkeletonItems = ({ count }: { count: number }) => {
    const skeletonTextProps = SKELETON_PROPS.text;
    return useMemo(() => {
        return Array.from(Array(count)).map((item, index) => {
            return (
                <Box key={index}>
                    <SkeletonText lineClamp={1} mb={5} w={'100%'} {...skeletonTextProps} />

                    <HStack align={'flex-start'} justify={'space-between'} gap={4}>
                        <Box width={220}>
                            <Skeleton w={'100%'} height={'124px'} mb={4} />

                            <SkeletonText
                                {...skeletonTextProps}
                                lineClamp={2}
                                mb={6}
                                maxW={'75%'}
                            />
                            <SkeletonText {...skeletonTextProps} lineClamp={3} maxW={'75%'} />
                        </Box>
                        <Flex
                            flex={1}
                            width={'100%'}
                            justify={'space-between'}
                            align={{ base: 'flex-end', lg: 'flex-start' }}
                            direction={{ base: 'column', lg: 'row' }}
                            pb={2}
                            gap={4}
                        >
                            <HStack
                                gap={4}
                                align={'flex-start'}
                                width={{ base: '100%', lg: undefined }}
                            >
                                <Box flex={2} minW={140}>
                                    <SkeletonText
                                        {...skeletonTextProps}
                                        lineClamp={2}
                                        maxW={'100px'}
                                    />
                                </Box>
                                <Box flex={5}>
                                    <SkeletonText
                                        {...skeletonTextProps}
                                        lineClamp={4}
                                        maxW={'120px'}
                                    />
                                </Box>
                            </HStack>

                            <Flex flex={5} direction={'column'} align={'flex-end'}>
                                <SkeletonText
                                    lineClamp={2}
                                    display={'flex'}
                                    alignItems={'flex-end'}
                                    flexDirection={'column'}
                                    w={'80px'}
                                    mb={4}
                                    {...skeletonTextProps}
                                />

                                <Skeleton>
                                    <Button w={'220px'} />
                                </Skeleton>
                            </Flex>
                        </Flex>
                    </HStack>
                </Box>
            );
        });
    }, [count]);
};

export const AccommodationPricing = ({
    bestOfferMobileProps,
    ...props
}: AccommodationPricingProps) => {
    const { t } = useTranslation('accommodation', {
        keyPrefix: 'pricing',
    });
    const dispatch = useAppDispatch();

    const currentAccommodation = useAppSelector(
        accommodationSlice.selectors.selectCurrentAccommodation,
    );
    const { roomCount } = useAppSelector(accommodationStaticSlice.selectors.selectRoomData);

    const [showAllItems, setShowAllItems] = useState<boolean>(false);
    const visibleItems = VISIBLE_PRICING_VARIANTS;

    const loadingState = useAppSelector(accommodationSlice.selectors.selectLoading);
    const isLoading = loadingState === LoadingState.PENDING;
    const skeletonItemCount = Math.min(roomCount, visibleItems);

    const isUnAvailable = useIsUnavailable();

    const { isDrawerPending, openDrawer } = useDrawerState(() => {
        dispatch(
            commonUiStateSlice.actions.setModalDataAction({
                modalName: AppModalsEnum.ApartmentsList,
                modalData: true,
            }),
        );
    });

    const isMobileRes = useBreakpointValue({
        base: true,
        md: false,
    });

    const renderMobileContent = () => (
        <Flex gap={'gutterWidth'} direction={'column'}>
            <BestOffer {...bestOfferMobileProps} />
            {currentAccommodation?.apartments?.length ? (
                <Button
                    colorPalette={'secondary'}
                    variant={'outline'}
                    onClick={openDrawer}
                    disabled={isDrawerPending}
                    loading={isDrawerPending}
                    _hover={{ bg: 'white' }}
                >
                    {t('showAllRooms', {
                        value: roomCount,
                    })}
                </Button>
            ) : null}
        </Flex>
    );

    const renderDesktopContent = () => (
        <Flex gap={'gutterWidth'} direction={'column'}>
            {isLoading ? (
                <SkeletonItems count={skeletonItemCount} />
            ) : (
                <>
                    {currentAccommodation?.apartments?.map((apartment, i) => (
                        <SingleApartment
                            key={apartment?.id}
                            apartment={apartment}
                            display={!showAllItems && i >= visibleItems ? 'none' : undefined}
                        />
                    ))}
                    {currentAccommodation?.apartments?.length && roomCount > visibleItems && (
                        <Button
                            colorPalette={'secondary'}
                            onClick={() => setShowAllItems(!showAllItems)}
                            alignSelf={'center'}
                        >
                            {showAllItems
                                ? t('showLessRooms')
                                : t('showAllRooms', {
                                      value: roomCount,
                                  })}
                        </Button>
                    )}
                </>
            )}
            <Separator />
        </Flex>
    );

    return <Box {...props}>{isMobileRes ? renderMobileContent() : renderDesktopContent()}</Box>;
};
